import React, { useState, useEffect } from "react";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { collection, addDoc, getDocs, serverTimestamp } from "firebase/firestore";
import { db, storage } from "../firebase.config";
import { useNavigate } from "react-router-dom";

function PhotoUpload2() {
    const [filesMetadata, setFilesMetadata] = useState([]); // Array of {file, caption}
    const [albums, setAlbums] = useState([]); // Derived from metadata
    const [selectedAlbum, setSelectedAlbum] = useState("General");
    const [newAlbumName, setNewAlbumName] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const navigate = useNavigate();

    // Fetch existing albums from photo metadata on mount
    useEffect(() => {
        const fetchAlbumsFromMetadata = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, "photos"));
                const existingAlbums = new Set(["General"]); // Default starting point
                
                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    if (data.album && data.album.trim() !== "") {
                        existingAlbums.add(data.album);
                    }
                });
                
                setAlbums(Array.from(existingAlbums).sort());
            } catch (error) {
                console.error("Error fetching album metadata:", error);
            }
        };
        fetchAlbumsFromMetadata();
    }, []);

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        const newMetadata = files.map(file => ({
            file: file,
            caption: ""
        }));
        setFilesMetadata(newMetadata);
    };

    const handleCaptionChange = (index, value) => {
        const updated = [...filesMetadata];
        updated[index].caption = value;
        setFilesMetadata(updated);
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (filesMetadata.length === 0) return alert("Please select files");

        setIsUploading(true);
        
        // Use the new typed name if provided, otherwise use the dropdown selection
        const finalAlbum = newAlbumName.trim() !== "" ? newAlbumName.trim() : selectedAlbum;

        const promises = filesMetadata.map((item) => {
            const storagePath = `images/${Date.now()}_${item.file.name}`;
            const storageRef = ref(storage, storagePath);
            const uploadTask = uploadBytesResumable(storageRef, item.file);

            return new Promise((resolve, reject) => {
                uploadTask.on(
                    "state_changed",
                    (snapshot) => {
                        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                        setUploadProgress(progress);
                    },
                    (error) => reject(error),
                    async () => {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                
                        await addDoc(collection(db, "photos"), {
                            src: downloadURL,
                            storagePath: storagePath,
                            caption: item.caption,
                            album: finalAlbum, 
                            createdAt: serverTimestamp()
                        });
                        resolve();
                    }
                );
            });
        });

        try {
            await Promise.all(promises);
            setIsUploading(false);
            navigate("/gallery"); 
        } catch (error) {
            console.error("Upload failed:", error);
            alert("Upload failed. Please try again.");
            setIsUploading(false);
        }
    };

    const handleCancel = () => {
        setFilesMetadata([]);
        setIsUploading(false);
        setUploadProgress(0);
        setNewAlbumName("");
    };

    return (
        <div className="box-layout">
            <div className="container-about">
                <h1 className="title-un-des">Upload Photos</h1>
                <div className="title-un-icon"></div>
                <br />
                <p style={{ textAlign: "center" }}>Select an existing album or create a new metadata tag.</p>
                
                <div className="container">
                    <form className="newsletter-container" onSubmit={handleUpload}>
                        
                        <div className="album-logic-row" style={{ marginBottom: "20px", display: "flex", gap: "10px", justifyContent: "center" }}>
                            <div>
                                <label>Select an Album: </label>
                                <select 
                                    className="custom-input" 
                                    value={selectedAlbum} 
                                    onChange={(e) => setSelectedAlbum(e.target.value)}
                                >
                                    {albums.map(a => <option key={a} value={a}>{a}</option>)}
                                </select>
                            </div>

                            <div>
                                <label>Or Create new Album: </label>
                                <input 
                                    className="custom-input"
                                    type="text" 
                                    placeholder="New album name..." 
                                    value={newAlbumName}
                                    onChange={(e) => setNewAlbumName(e.target.value)}
                                />
                            </div>
                        </div>

                        <div style={{ textAlign: "center", marginBottom: "20px" }}>
                            <label htmlFor="photoinput" className="button">
                                Choose Files ({filesMetadata.length} selected)
                            </label>
                            <input
                                id="photoinput"
                                style={{ display: "none" }}
                                type="file"
                                multiple
                                onChange={handleFileChange}
                            />
                        </div>

                        {/* Optional: Preview / Captioning area */}
                        {filesMetadata.length > 0 && (
                            <div className="upload-previews" style={{ marginBottom: "20px" }}>
                                {filesMetadata.map((item, index) => (
                                    <div key={index} style={{ marginBottom: "10px", display: "flex", alignItems: "center", gap: "10px" }}>
                                        <small>{item.file.name}</small>
                                        <input 
                                            placeholder="Add a caption..." 
                                            className="custom-input"
                                            value={item.caption}
                                            onChange={(e) => handleCaptionChange(index, e.target.value)}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        <div style={{ textAlign: "center" }}>
                            <button
                                type="submit"
                                className="button green-button"
                                disabled={isUploading || filesMetadata.length === 0}
                            >
                                {isUploading ? "Uploading..." : "Submit"}
                            </button>
                            &nbsp;
                            <button
                                type="button"
                                className="button"
                                onClick={handleCancel}
                                disabled={isUploading || filesMetadata.length === 0}
                            >
                                Clear
                            </button>
                        </div>
                    </form>

                    {isUploading && (
                        <div className="upload-progress" style={{ textAlign: "center", marginTop: "20px" }}>
                            <progress value={uploadProgress} max="100" style={{ width: "100%" }}></progress>
                            <p>{uploadProgress}% Complete</p>
                        </div>
                    )}
                </div>
            </div>  
        </div>
    );
}

export default PhotoUpload2;